// Where the marks on a review's progress bar sit, and what each one opens
// (owner's rules, 2026-10-05). A mark is a point on the bar: when an answer
// takes the learner's progress across it, the next press of "next" opens the
// clinical exam station (OSCE practice) or the one-minute voice check.
//
//   Both a station and the voice check: the station comes first.
//     under 40 questions:  station at 50%, voice check at 75%
//     40 questions or more: station at 35%, voice check at 70%
//   The voice check alone:
//     under 40 questions: 70%
//     40 to 250 questions: 50%
//     more than 250 questions: 35%
//
// The positions come from how far learners get: of those who answer a first
// question, about half reach 50% of a set and fewer than a third finish it,
// and long sets are left earlier.
//
// Pure, with no imports: the code that notices a crossing runs at app startup.

export type ReviewProgressMarkKind = "clinical_exam_station" | "voice_check";

export interface ReviewProgressMark {
  kind: ReviewProgressMarkKind;
  // 0 to 100, the same scale as the bar.
  percentage: number;
}

export const QUESTION_COUNT_FROM_WHICH_MARKS_SIT_EARLIER = 40;
export const QUESTION_COUNT_ABOVE_WHICH_A_LONE_VOICE_CHECK_SITS_EARLIEST = 250;

export function getReviewProgressMarks({
  numberOfQuestions,
  hasClinicalExamStation,
  hasVoiceCheck,
}: {
  numberOfQuestions: number;
  hasClinicalExamStation: boolean;
  hasVoiceCheck: boolean;
}): ReviewProgressMark[] {
  if (!Number.isFinite(numberOfQuestions) || numberOfQuestions <= 0) return [];
  const isShortReview = numberOfQuestions < QUESTION_COUNT_FROM_WHICH_MARKS_SIT_EARLIER;

  if (hasClinicalExamStation) {
    const marks: ReviewProgressMark[] = [{ kind: "clinical_exam_station", percentage: isShortReview ? 50 : 35 }];
    if (hasVoiceCheck) marks.push({ kind: "voice_check", percentage: isShortReview ? 75 : 70 });
    return marks;
  }

  if (!hasVoiceCheck) return [];
  if (isShortReview) return [{ kind: "voice_check", percentage: 70 }];
  if (numberOfQuestions > QUESTION_COUNT_ABOVE_WHICH_A_LONE_VOICE_CHECK_SITS_EARLIEST) {
    return [{ kind: "voice_check", percentage: 35 }];
  }
  return [{ kind: "voice_check", percentage: 50 }];
}

// The marks an answer has just taken the learner across: below the mark
// before, at or past it now. Lowest first. Progress that falls (a card
// answered wrong that used to be right) crosses nothing.
export function getReviewProgressMarksCrossed({
  marks,
  previousProgressPercentage,
  newProgressPercentage,
}: {
  marks: ReviewProgressMark[];
  previousProgressPercentage: number;
  newProgressPercentage: number;
}): ReviewProgressMark[] {
  return marks
    .filter((mark) => previousProgressPercentage < mark.percentage && newProgressPercentage >= mark.percentage)
    .sort((firstMark, secondMark) => firstMark.percentage - secondMark.percentage);
}
